import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';

import { Form, Row } from 'react-bootstrap';
import SimpleMDE from 'react-simplemde-editor';
import ReactMarkdown from 'react-markdown';
import 'easymde/dist/easymde.min.css';
import { toast } from 'react-toastify';

import styles from './EditSchema.module.scss';
import { useTranslate } from 'react-polyglot';
import { useAuth } from '../../../../../Auth';
import { useAxios } from '../../../../../AxiosHandler';
import CustomTraitEditor from './CustomTraitEditor/CustomTraitEditor';

function EditSchema(props: EditSchemaProps) {
    const t = useTranslate();
    const navigate = useNavigate();
    const auth = useAuth();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;
    const params = useParams();

    const [originalSchema, setOriginalSchema] = useState<any | undefined>();
    const [schema, setSchema] = useState<any | undefined>();
    
    const [traits, setTraits] = useState<any | undefined>();
    const [customTraits, setCustomTraits] = useState<any | undefined>();

    const [newBreedId, setNewBreedId] = useState<string>("");

    const [editingCustomTrait, setEditingCustomTrait] = useState<string | undefined>();
    
    // Memoize the SimpleMDE options to prevent unnecessary re-renders
    const mdeOptions = useMemo(() => {
        return {
            placeholder: t("admin.editdynschema.edit.gdprplaceholder") || "Optional GDPR information to display to users",
            spellChecker: false,
            autofocus: false,
            status: false,
            minHeight: "100px", // Set minimum height for the editor
            lineNumbers: false, // Hide line numbers to save space
            initialHeight: "5", // Initial height as 5 rows
            hideIcons: [
                "image",
            ] as any,
            previewRender(text: string) {
                return ReactDOMServer.renderToString(
                    <ReactMarkdown>
                        {text}
                    </ReactMarkdown>
                );
            }
        };
    }, [t]); // Only re-create when translations change

    function getSchema() {
        axios.single({
            method: 'get',
            url: "/api/dynschema/" + auth.selectedBreed + "/" + params.dynschemaid,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {

            res.data.questions.map((question: any) => {
                question.traitType = question.CUSTOMTRAIT_ID != null ? "custom" : "trait";
                if (question.CUSTOMTRAIT_ID == null && question.TRAIT_ID == null) {
                    question.traitType = "none";
                }

                return question;
            });

            setOriginalSchema(JSON.parse(JSON.stringify(res.data)));
            setSchema(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function getCustomTraits() {
        axios.single({
            method: 'get',
            url: "/api/dynschema/customtraits/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setCustomTraits(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function createSchema() {
        axios.single({
            method: 'put',
            url: "/api/dynschema/addschema/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
            data: schema
        }).then((res: any) => {
            setSchema({ ...schema, ID: res.data });
        }).catch((err: any) => {
            console.log(err);
        });
    }
    
    function saveSchema() {
        axios.single({
            method: 'post',
            url: "/api/dynschema/updateschema/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" },
            data: {
                schema: schema,
                originalSchema: JSON.parse(JSON.stringify(originalSchema))
            }
        }).then((res: any) => {
            if (res.status < 400) {
                getSchema();
                toast.success(t("admin.editdynschema.success.saved") || "Schema saved successfully.");
            } else {
                toast.error(t("admin.editdynschema.error.savefailed") || "Failed to save schema. Please try again.");
            }
        }).catch((err: any) => {
            console.log(err);
            toast.error(t("admin.editdynschema.error.savefailed") || "Failed to save schema. Please try again.");
        });
    }

    function deleteSchema() {
        if (window.confirm(t("admin.editdynschema.confirmdeleteschema"))) {
            axios.single({
                method: 'delete',
                url: "/api/dynschema/deleteschema/" + schema.ID + "/" + auth.selectedBreed,
                responseType: 'json',
                headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
            }).then((res: any) => {
                navigate("/admin");
            }).catch((err: any) => {
                console.log(err);
            });
        }
    }

    function duplicateSchema() {
        axios.single({
            method: 'put',
            url: "/api/dynschema/duplicateschema/" + schema.ID + "/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            navigate("/editdynschema/" + res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }

    function getTraits() {
        setTraits(auth.availableBreeds?.find((breed: any) => breed.ID == auth.selectedBreed)?.TRAITS);
    }

    function hasTraitError(question: any) {
        // If there are more than one of the same trait in the schema, return true
        let count = 0;
        schema.questions.forEach((q: any) => {
            if (q.TRAIT_ID != null && q.TRAIT_ID == question.TRAIT_ID) count++;
        });
        schema.questions.forEach((q: any) => {
            if (q.CUSTOMTRAIT_ID != null && q.CUSTOMTRAIT_ID == question.CUSTOMTRAIT_ID) count++;
        });
        return count > 1;
    }

    function drawQuestionEditor(question: any, index: number) {
        return (
            <div key={index} className={styles.questioneditor}>
                <div className={styles.questioneditorcontent}>
                    <div className={styles.titleeditor}>
                        <label>{t("admin.editdynschema.question.type")}</label>
                        <select
                            className="form-control"
                            value={question.TYPE}
                            onChange={(e) => {
                                let newSchema = schema;
                                newSchema.questions[index].TYPE = e.target.value;
                                setSchema(JSON.parse(JSON.stringify(newSchema)));
                            }}
                        >
                            <option value="question">{t("admin.editdynschema.questiontype.question")}</option>
                            <option value="title">{t("admin.editdynschema.questiontype.title")}</option>
                            <option value="divider">{t("admin.editdynschema.questiontype.divider")}</option>
                        </select>
                        <br/>
                        { question.TYPE != "divider" ?
                            <>
                                <label>{ question.TYPE == "title" ? t("admin.editdynschema.question.title_title") : t("admin.editdynschema.question.title")}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={question.TEXT}
                                    onChange={(e) => {
                                        let newSchema = schema;
                                        newSchema.questions[index].TEXT = e.target.value;
                                        setSchema(JSON.parse(JSON.stringify(newSchema)));
                                    }}
                                />
                                <label>{ question.TYPE == "title" ? t("admin.editdynschema.question.title_description") : t("admin.editdynschema.question.description")}</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={question.DESCRIPTION || ""}
                                    onChange={(e) => {
                                        let newSchema = schema;
                                        newSchema.questions[index].DESCRIPTION = e.target.value;
                                        setSchema(JSON.parse(JSON.stringify(newSchema)));
                                    }}
                                />
                            </>
                        : null }
                    </div>
                    <div className={styles.traiteditor}>
                        {question.TYPE != "divider" && question.TYPE != "title" &&
                            <>
                                <label>{t("admin.editdynschema.question.traittype")}</label>
                                <select
                                    className="form-control"
                                    value={question.traitType}
                                    onChange={(e) => {
                                        let newSchema = schema;
                                        newSchema.questions[index].traitType = e.target.value;
                                        newSchema.questions[index].TRAIT_ID = null;
                                        newSchema.questions[index].CUSTOMTRAIT_ID = null;
                                        setSchema(JSON.parse(JSON.stringify(newSchema)));
                                    }}
                                >
                                    <option value="none">{t("admin.editdynschema.questiontraittype.select")}</option>
                                    <option value="trait">{t("admin.editdynschema.questiontraittype.trait")}</option>
                                    <option value="custom">{t("admin.editdynschema.questiontraittype.customtrait")}</option>
                                </select>
                                <label>{t("admin.editdynschema.question.trait")}</label>
                                <Form.Select
                                    className={"form-control " + (hasTraitError(question) ? styles.selecterror : "")}
                                    value={question.traitType == "trait" ? question.TRAIT_ID : question.CUSTOMTRAIT_ID}
                                    onChange={(e) => {
                                        if (e.target.value == "new") {
                                            setEditingCustomTrait("new");
                                            let newSchema = schema;
                                            if (question.traitType == "trait") {
                                                newSchema.questions[index].TRAIT_ID = "";
                                            }
                                            if (question.traitType == "custom") {
                                                newSchema.questions[index].CUSTOMTRAIT_ID = "";
                                            }
                                            setSchema(JSON.parse(JSON.stringify(newSchema)));
                                            return;
                                        }

                                        // Not a new trait, set the trait to the selected value
                                        let newSchema = schema;
                                        if (question.traitType == "trait") {
                                            newSchema.questions[index].TRAIT_ID = e.target.value;
                                        } else {
                                            newSchema.questions[index].CUSTOMTRAIT_ID = e.target.value;
                                        }
                                        let trait = traits?.find((trait: any) => trait.ID == e.target.value);
                                        let customtrait = customTraits?.find((trait: any) => trait.ID == e.target.value);

                                        if (question.traitType == "trait") {
                                            newSchema.questions[index].type = trait.DATATYPE;
                                            newSchema.questions[index].TRAIT_DATATYPE = trait.DATATYPE;
                                            newSchema.questions[index].TRAIT_OPTIONS = trait.OPTIONS;
                                        } else {
                                            newSchema.questions[index].type = customtrait.DATATYPE;
                                            newSchema.questions[index].CUSTOMTRAIT_DATATYPE = customtrait.DATATYPE;
                                            newSchema.questions[index].CUSTOMTRAIT_OPTIONS = customtrait.OPTIONS;
                                        }

                                        setSchema(JSON.parse(JSON.stringify(newSchema)));
                                    }}
                                >
                                    <option value="">{t("admin.editdynschema.questiontrait.select")}</option>
                                    { question.traitType == "custom" ?
                                        <>
                                            <option value="divider" disabled>---</option>
                                            <option value="new">{t("admin.editdynschema.questiontrait.newtrait")}</option>
                                            <option value="divider" disabled>---</option>
                                        </>
                                    : null }
                                    {question.traitType == "trait" && traits?.map((trait: any) => {
                                        return <option key={trait.ID} value={trait.ID}>{t("dogs.table." + trait.NAME)}</option>;
                                    })}
                                    {question.traitType == "custom" && customTraits?.map((trait: any) => {
                                        return <option key={trait.ID} value={trait.ID}>{trait.NAME}</option>;
                                    })}
                                </Form.Select>
                                { question.traitType == "custom" && question.CUSTOMTRAIT_ID != null ?
                                    <button
                                        className={"btn btn-positive " + styles.editcustomtraitbtn}
                                        onClick={() => {
                                            setEditingCustomTrait(question.CUSTOMTRAIT_ID);
                                        }}
                                    >
                                        {t("admin.editdynschema.question.editcustomtrait")}
                                    </button>
                                : null }
                            </>
                        }
                    </div>
                    {/* Move Up button */}
                    <button
                        className={"btn btn-primary " + styles.minibutton}
                        onClick={() => {
                            let newSchema = schema;
                            if (index > 0) {
                                let temp = newSchema.questions[index];
                                newSchema.questions[index] = newSchema.questions[index - 1];
                                newSchema.questions[index - 1] = temp;
                                // Recalculate all ORDERING values
                                newSchema.questions.forEach((q: any, i: number) => {
                                    q.ORDERING = i + 1;
                                });
                                setSchema(JSON.parse(JSON.stringify(newSchema)));
                            }
                        }}
                    >↑</button>
                    {/* Move Down button */}
                    <button
                        className={"btn btn-primary " + styles.minibutton}
                        onClick={() => {
                            let newSchema = schema;
                            if (index < newSchema.questions.length - 1) {
                                let temp = newSchema.questions[index];
                                newSchema.questions[index] = newSchema.questions[index + 1];
                                newSchema.questions[index + 1] = temp;
                                // Recalculate all ORDERING values
                                newSchema.questions.forEach((q: any, i: number) => {
                                    q.ORDERING = i + 1;
                                });
                                setSchema(JSON.parse(JSON.stringify(newSchema)));
                            }
                        }}
                    >↓</button>
                    {/* Delete Question button */}
                    <button
                        className={"btn btn-negative " + styles.minibutton}
                        onClick={() => {
                            let newSchema = schema;
                            newSchema.questions.splice(index, 1);
                            // Recalculate all ORDERING values
                            newSchema.questions.forEach((q: any, i: number) => {
                                q.ORDERING = i + 1;
                            });
                            setSchema(JSON.parse(JSON.stringify(newSchema)));
                        }}
                    >-</button>
                </div>
            </div>
        );
    }

    function drawSchemaEditor() {
        return (
            <div>
                <label>{t("admin.editdynschema.edit.title")}</label>
                <input
                    type="text"
                    className="form-control"
                    value={schema.TITLE}
                    onChange={(e) => {
                        let newSchema = schema;
                        newSchema.TITLE = e.target.value;
                        setSchema(JSON.parse(JSON.stringify(newSchema)));
                    }}
                />                <label>{t("admin.editdynschema.edit.description")}</label>
                <input
                    type="text"
                    className="form-control"
                    value={schema.DESCRIPTION}
                    onChange={(e) => {
                        let newSchema = schema;
                        newSchema.DESCRIPTION = e.target.value;
                        setSchema(JSON.parse(JSON.stringify(newSchema)));
                    }}
                />                <label>
                    {t("admin.editdynschema.edit.gdpr") || "GDPR Text"}
                    <a 
                        href="https://www.markdownguide.org/basic-syntax/" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={styles.markdownLink}
                    >
                        -(Markdown)
                    </a>
                </label>
                <SimpleMDE
                    value={schema.GDPR || ""}
                    onChange={(value) => {
                        let newSchema = schema;
                        newSchema.GDPR = value;
                        setSchema(JSON.parse(JSON.stringify(newSchema)));
                    }}
                    options={mdeOptions}
                />
                <hr/>
                <div className={styles.schemaeditor}>
                    {schema.questions.map((question: any, index: number) => {
                        return drawQuestionEditor(question, index);
                    })}
                </div>
                <button className="btn btn-positive" onClick={() => {
                    let newSchema = schema;
                    newSchema.questions.push({
                        TEXT: "",
                        DESCRIPTION: "",
                        ORDERING: newSchema.questions.length + 1,
                        TYPE: "question",
                        type: "string",
                        traitType: "none"
                    });
                    setSchema(JSON.parse(JSON.stringify(newSchema)));
                }}>+</button>
            </div>
        );
    }

    function drawBreedList() {
        return (
            <div className={styles.breedlist}>
                <label>{t("admin.editdynschema.breedlist.title")}</label>
                <ul>
                    {schema.BREED_IDS?.split(",")?.filter((i: any) => i != "")?.map((breedId: any) => (
                        <li key={breedId}>
                            {breedId} ({ params.dynschemaid == "new" ?
                                <span>{t("admin.editdynschema.breedlist.saveforlink")}</span>
                            :
                                <a target='_blank' href={"/skjema/dyn/" + breedId + "/" + params.dynschemaid}>Link</a>
                            })
                            <span
                                className="fa fa-trash"
                                onClick={() => {
                                    let newSchema = schema;
                                    newSchema.BREED_IDS = newSchema.BREED_IDS.split(",").filter((id: any) => id !== breedId).join(",");
                                    setSchema(JSON.parse(JSON.stringify(newSchema)));
                                }}
                            ></span>
                        </li>
                    ))}
                </ul>
                <div className={styles.addbreed}>
                    <input
                        type="text"
                        className="form-control"
                        placeholder={t("admin.editdynschema.breedlist.addbreed")}
                        value={newBreedId}
                        onChange={(e) => setNewBreedId(e.target.value)}
                    />
                    <button
                        className="btn btn-positive"
                        onClick={() => {
                            if (newBreedId) {
                                let newSchema = schema;
                                newSchema.BREED_IDS = newSchema.BREED_IDS ? `${newSchema.BREED_IDS},${newBreedId}` : newBreedId;
                                setSchema(JSON.parse(JSON.stringify(newSchema)));
                                setNewBreedId("");
                            }
                        }}
                    >
                        {t("admin.editdynschema.breedlist.addbutton")}
                    </button>
                </div>
            </div>
        );
    }

    useEffect(() => {
        if (auth.user?.userdata?.ROLE != "sysadmin") {
            navigate("/");
        }
    }, []);

    useEffect(() => {
        // If params.dynschemaid is new, change it to this schema's ID if it exists
        if (params.dynschemaid == "new" && schema?.ID) {
            navigate("/editdynschema/" + schema.ID);
        }
    }, [schema]);

    useEffect(() => {
        if (params.dynschemaid && params.dynschemaid != "new" && auth.selectedBreed) getSchema();
        if (params.dynschemaid == "new" && auth.selectedBreed) {
            setSchema({
                TITLE: "",
                DESCRIPTION: "",
                questions: []
            });
        }
        if (auth.availableBreeds && auth.availableBreeds.length > 0 && auth.selectedBreed) getTraits();
        if (auth.selectedBreed) getCustomTraits();
    }, [params.dynschemaid, auth.selectedBreed, auth.availableBreeds]);

    return (
        <div className="maincontent">
            <div className={styles.editschema}>
                <h1>{t("admin.editdynschema.title")}</h1>
                <hr />
                <button className="btn btn-primary" onClick={() => navigate("/admin")}>{t("admin.editdynschema.buttons.back")}</button>
                { schema != undefined && schema?.SPECIAL_SCHEMA_TYPE == null ?
                    <button className="btn btn-negative" onClick={() => deleteSchema()}>{t("admin.editdynschema.buttons.deleteschema")}</button>
                : null }
                { schema != undefined && schema?.SPECIAL_SCHEMA_TYPE == null ?
                    <button className="btn btn-warning" onClick={() => duplicateSchema()}>{t("admin.editdynschema.buttons.duplicateschema")}</button>
                : null }
                { schema != undefined ?
                    <button className="btn btn-positive" onClick={() => {
                        if (params.dynschemaid == "new") {
                            createSchema();
                        } else {
                            saveSchema();
                        }
                    }}>{t("admin.editdynschema.buttons.saveschema")}</button>
                : null }
                <hr/>
                {schema && drawBreedList()}
                <hr/>
                {schema && drawSchemaEditor()}
                <hr/>
                {/* Save button */}
                <button className="btn btn-positive" onClick={() => {
                    if (params.dynschemaid == "new") {
                        createSchema();
                    } else {
                        saveSchema();
                    }
                }}>{t("admin.editdynschema.buttons.saveschema")}</button>
            </div>
            <CustomTraitEditor
                editingCustomTrait={editingCustomTrait}
                onClose={() => {
                    setEditingCustomTrait(undefined);
                    getCustomTraits();
                }}
                onSave={(traitid) => {
                    setEditingCustomTrait(undefined);
                    getCustomTraits();
                }}
            ></CustomTraitEditor>
        </div>
    );
}

class EditSchemaProps {
    
}

export default EditSchema;